import { Link } from "react-router-dom";
import { ArrowRight, Phone, Leaf, Award, UtensilsCrossed, Quote, Star, Smartphone, Users, Mail, CalendarDays, Building2 } from "lucide-react";
import { HERO, HOME_PAGE } from "@/constants/testIds";
import { RESTAURANT } from "@/data/menu";
import Reveal from "@/components/site/Reveal";

const HERO_IMG = "/images/hero-food.png";
const ABOUT_IMG = "/images/about-curry.png";
const CATERING_IMG = "/images/catering-spread.png";
const FEATURED_PANEER = "/images/featured-paneer-tikka.png";
const FEATURED_THALI = "/images/featured-misal-pav.png";
const FEATURED_THIRD = "/images/featured-veg-biryani.png";
const FEATURED_RICE_PLATE = "/images/featured-rice-plate.png";
const FEATURED_MINI_PUNJABI = "/images/featured-mini-punjabi.png";
const FEATURED_DAL_KHICHADI = "/images/featured-dal-khichadi.png";

const featured = [
  { name: "Paneer Tikka Masala", desc: "Tandoor-charred paneer in a rich, smoky tomato gravy. Best paired with garlic naan.", img: FEATURED_PANEER },
  { name: "Misal Pav", desc: "Pune-style spiced sprouts curry topped with crisp farsan, served warm with soft pav.", img: FEATURED_THALI },
  { name: "Veg Biryani", desc: "Layered basmati with vegetables, mint and biryani masala — slow-steamed to order.", img: FEATURED_THIRD },
  { name: "Rice Plate", desc: "Dal, rice, sabji, chapati, papad and dahi — a complete homestyle meal on one plate.", img: FEATURED_RICE_PLATE },
  { name: "Mini Punjabi", desc: "A compact Punjabi platter with rich sabji, dal tadka, jeera rice and warm parathas.", img: FEATURED_MINI_PUNJABI },
  { name: "Dal Khichadi", desc: "One-pot rice and moong dal comfort, finished with a generous spoon of ghee.", img: FEATURED_DAL_KHICHADI },
];

const testimonials = [
  {
    quote:
      "Walking into Giridhar feels like walking into a Maharashtrian home — the warmth, the aromas, the unhurried hospitality.",
    name: "Aarti Deshpande",
    detail: "Food Writer, Pune",
  },
  {
    quote:
      "Three decades of consistency. The misal here is the benchmark by which I judge every other version in the city.",
    name: "Aditya Bafana",
    detail: "Regular guest since 2008",
  },
  {
    quote:
      "Pure vegetarian, deeply rooted, quietly warm. The Sunday thali is a ritual I don't miss.",
    name: "Meera Iyer",
    detail: "Bengaluru, visiting Pune",
  },
];

export default function HomePage() {
  return (
    <div data-testid={HOME_PAGE.page}>
      {/* HERO */}
      <section
        data-testid={HERO.section}
        className="relative min-h-[88vh] w-full overflow-hidden flex items-center"
      >
        <div
          className="absolute inset-0 bg-center bg-cover scale-105"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/45 to-black/70" aria-hidden="true" />
        <div className="absolute inset-0 grain pointer-events-none" aria-hidden="true" />

        <div className="container-x relative z-10 py-32 max-w-3xl">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cream/12 border border-cream/25 text-cream/90 backdrop-blur-sm text-xs tracking-[0.22em] uppercase">
              <Leaf className="w-3.5 h-3.5" /> Pure Vegetarian • Est. 1991
            </div>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl text-cream leading-[1.05] text-balance">
              A taste of Pune,
              <br />
              <span className="italic text-cream/95">three generations</span> in the making.
            </h1>
          </Reveal>
          <Reveal delay={220}>
            <p className="mt-6 text-base sm:text-lg text-cream/85 max-w-xl leading-relaxed">
              {RESTAURANT.tagline}. Slow-cooked recipes, hand-stretched breads,
              and the unhurried hospitality of a family kitchen — every single day.
            </p>
          </Reveal>
          <Reveal delay={320}>
            <div className="mt-10 flex flex-wrap items-center gap-3 sm:gap-4">
              <Link
                to="/menu"
                data-testid={HERO.ctaMenu}
                className="inline-flex items-center gap-2 rounded-full bg-cream text-brand hover:bg-cream/90 px-7 py-3.5 text-sm font-medium tracking-wide transition-colors"
              >
                Explore the Menu <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#app"
                data-testid={HERO.ctaContact}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("app")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="inline-flex items-center gap-2 rounded-full border border-cream/40 text-cream hover:bg-cream/10 px-7 py-3.5 text-sm font-medium tracking-wide transition-colors"
              >
                <Smartphone className="w-4 h-4" /> Order on App
              </a>
            </div>
          </Reveal>

          <Reveal delay={420}>
            <div className="mt-14 flex flex-wrap gap-x-10 gap-y-4 text-cream/75 text-sm">
              <div>
                <p className="font-serif text-3xl text-cream">34+</p>
                <p className="uppercase tracking-[0.18em] text-[0.7rem] mt-1">Years of service</p>
              </div>
              <div>
                <p className="font-serif text-3xl text-cream">40+</p>
                <p className="uppercase tracking-[0.18em] text-[0.7rem] mt-1">Vegetarian dishes</p>
              </div>
              <div>
                <p className="font-serif text-3xl text-cream">4.7★</p>
                <p className="uppercase tracking-[0.18em] text-[0.7rem] mt-1">Across guest reviews</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ABOUT */}
      <section
        data-testid={HOME_PAGE.about}
        className="section-y container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center"
      >
        <Reveal className="lg:col-span-6 order-2 lg:order-1">
          <p className="text-xs tracking-[0.24em] uppercase text-brand font-medium">Our Story</p>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-ink leading-[1.08] text-balance">
            A kitchen that has stayed
            <br /> stubbornly, beautifully, the same.
          </h2>
          <div className="hairline my-8 w-24" />
          <p className="text-ink-muted text-base sm:text-lg leading-relaxed">
            Founded in 1991 by Shri Bhivlal Bishnoi as a modest neighbourhood
            kitchen in Aundh, Giridhar Veg Restaurant grew through word-of-mouth
            and the daily rhythm of regulars who came back for the dal that
            simmered from dawn and the rotis that arrived warm at the table.
          </p>
          <p className="mt-4 text-ink-muted text-base sm:text-lg leading-relaxed">
            Today we are a little larger and a lot busier — but the recipes,
            the wood-fired tandoor, and the second-generation cooks who learned
            from Bishnoi-ji himself, remain the same.
          </p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { icon: Award, title: "Since 1991", note: "Three decades, one kitchen." },
              { icon: Leaf, title: "100% Pure Veg", note: "No exceptions, ever." },
              { icon: UtensilsCrossed, title: "Slow Cooked", note: "Heritage, not haste." },
            ].map((f) => (
              <div
                key={f.title}
                className="rounded-2xl bg-white border border-borderc/60 p-5 shadow-[0_1px_0_0_rgba(0,0,0,0.02)] hover:shadow-md transition-shadow"
              >
                <span className="grid place-items-center w-10 h-10 rounded-full bg-brand/10 text-brand">
                  <f.icon className="w-5 h-5" />
                </span>
                <p className="mt-4 font-serif text-lg text-ink">{f.title}</p>
                <p className="text-sm text-ink-muted mt-1">{f.note}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={150} className="lg:col-span-6 order-1 lg:order-2">
          <div className="relative">
            <div className="absolute -inset-6 bg-brand/8 rounded-[2.5rem] -rotate-2" aria-hidden="true" />
            <img
              src={ABOUT_IMG}
              alt="Authentic Indian vegetarian curry served with naan"
              className="relative w-full h-[480px] lg:h-[560px] object-cover rounded-[2rem] shadow-xl"
              loading="lazy"
            />
            <div className="absolute -bottom-6 -left-6 hidden sm:block bg-cream border border-borderc rounded-2xl px-5 py-4 shadow-lg">
              <p className="text-xs uppercase tracking-[0.2em] text-ink-muted">Established</p>
              <p className="font-serif text-3xl text-brand">1991</p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* FEATURED DISHES */}
      <section data-testid={HOME_PAGE.featured} className="bg-cream-deep/60">
        <div className="section-y container-x">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
            <Reveal>
              <p className="text-xs tracking-[0.24em] uppercase text-brand font-medium">Chef's Selection</p>
              <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-ink leading-[1.08] max-w-xl text-balance">
                Signature plates, loved across three decades.
              </h2>
            </Reveal>
            <Reveal delay={150}>
              <Link
                to="/menu"
                className="inline-flex items-center gap-2 text-brand hover:text-brand-hover font-medium tracking-wide group"
              >
                View full menu
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {featured.map((d, i) => (
              <Reveal key={d.name} delay={i * 120}>
                <article
                  data-testid={HOME_PAGE.featuredCard}
                  className="group bg-white rounded-3xl overflow-hidden border border-borderc/70 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500"
                >
                  <div className="relative h-72 overflow-hidden">
                    <img
                      src={d.img}
                      alt={d.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1100ms]"
                      loading="lazy"
                    />
                    <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cream/95 text-brand text-[0.7rem] tracking-[0.18em] uppercase">
                      <Leaf className="w-3 h-3" /> Pure Veg
                    </span>
                  </div>
                  <div className="p-6 sm:p-7">
                    <h3 className="font-serif text-2xl text-ink">{d.name}</h3>
                    <p className="mt-3 text-ink-muted leading-relaxed">{d.desc}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid={HOME_PAGE.testimonials} className="section-y container-x">
        <div className="text-center max-w-2xl mx-auto">
          <Reveal>
            <p className="text-xs tracking-[0.24em] uppercase text-brand font-medium">Guest Words</p>
            <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-ink leading-[1.08] text-balance">
              The kindest thing is when guests come home.
            </h2>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {testimonials.map((t, i) => (
            <Reveal key={t.name} delay={i * 120}>
              <figure
                data-testid={HOME_PAGE.testimonialCard}
                className="h-full flex flex-col bg-white rounded-3xl p-7 lg:p-8 border border-borderc/70 shadow-sm hover:shadow-md transition-shadow"
              >
                <Quote className="w-7 h-7 text-brand/60" />
                <blockquote className="mt-5 text-ink leading-relaxed font-serif text-lg italic">
                  "{t.quote}"
                </blockquote>
                <div className="mt-auto pt-6 flex items-center justify-between">
                  <figcaption>
                    <p className="font-medium text-ink">{t.name}</p>
                    <p className="text-xs text-ink-muted mt-0.5">{t.detail}</p>
                  </figcaption>
                  <div className="flex gap-0.5 text-brand">
                    {[...Array(5)].map((_, k) => (
                      <Star key={k} className="w-3.5 h-3.5 fill-current" />
                    ))}
                  </div>
                </div>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MOBILE APP — rendered globally via Layout */}

      {/* BULK / CATERING */}
      <section
        data-testid={HOME_PAGE.catering}
        className="container-x section-y"
      >
        <Reveal>
          <div className="relative overflow-hidden rounded-[2.5rem] bg-white border border-borderc/60 shadow-sm">
            <div className="grid grid-cols-1 lg:grid-cols-12">
              <div className="lg:col-span-6 relative h-64 sm:h-80 lg:h-auto min-h-[320px]">
                <img
                  src={CATERING_IMG}
                  alt="Catering buffet spread of Indian vegetarian dishes"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                <span className="absolute top-5 left-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cream/95 text-brand text-[0.7rem] uppercase tracking-[0.18em] backdrop-blur-sm">
                  <Users className="w-3.5 h-3.5" />
                  Bulk &amp; Catering
                </span>
              </div>

              <div className="lg:col-span-6 p-8 sm:p-12 lg:p-14 flex flex-col justify-center">
                <p className="text-xs tracking-[0.24em] uppercase text-brand font-medium">
                  Feeding a crowd?
                </p>
                <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-ink leading-[1.08] text-balance">
                  Catering, the
                  <br />
                  <span className="italic">Giridhar way.</span>
                </h2>
                <p className="mt-5 text-ink-muted text-base sm:text-lg leading-relaxed max-w-lg">
                  Whether it's an office lunch, a family function, a wedding
                  rehearsal or a community gathering — we cater across Pune
                  with the same kitchen, the same recipes, and the same
                  warmth that has fed regulars for thirty-four years.
                </p>

                <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 max-w-md">
                  {[
                    { icon: Building2, label: "Office lunches & meetings" },
                    { icon: CalendarDays, label: "Weddings & receptions" },
                    { icon: Users, label: "Family functions & pujas" },
                    { icon: UtensilsCrossed, label: "Custom Indian veg menus" },
                  ].map((f) => (
                    <li
                      key={f.label}
                      className="flex items-start gap-2.5 text-sm text-ink"
                    >
                      <f.icon className="w-4 h-4 mt-0.5 text-brand shrink-0" />
                      {f.label}
                    </li>
                  ))}
                </ul>

                <div className="mt-9 flex flex-wrap gap-3">
                  <a
                    href={`tel:${RESTAURANT.phoneRaw}`}
                    data-testid={HOME_PAGE.cateringCallBtn}
                    className="inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand-hover text-cream px-7 py-3.5 text-sm font-medium tracking-wide transition-colors"
                  >
                    <Phone className="w-4 h-4" /> Call to enquire
                  </a>
                  <a
                    href={`mailto:${RESTAURANT.email}?subject=${encodeURIComponent("Catering enquiry — Giridhar Veg")}`}
                    data-testid={HOME_PAGE.cateringEmailBtn}
                    className="inline-flex items-center gap-2 rounded-full border border-brand/30 text-brand hover:bg-brand/5 px-7 py-3.5 text-sm font-medium tracking-wide transition-colors"
                  >
                    <Mail className="w-4 h-4" /> Email us
                  </a>
                </div>
                <p className="mt-4 text-xs text-ink-muted">
                  Typical lead time — 24 to 48 hours. Larger events: a week
                  ahead is ideal.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* FINAL CTA */}
      <section data-testid={HOME_PAGE.cta} className="container-x pb-24">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2.5rem] bg-[#0F1F11] text-cream px-8 sm:px-14 py-16 sm:py-20">
            <div
              className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-brand/40 blur-3xl"
              aria-hidden="true"
            />
            <div
              className="absolute -left-20 -bottom-20 w-72 h-72 rounded-full bg-accent2/30 blur-3xl"
              aria-hidden="true"
            />
            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="font-serif text-4xl sm:text-5xl leading-[1.08] text-balance">
                  Hungry already?
                  <br />
                  <span className="italic text-cream/85">A table is always warm at Giridhar.</span>
                </h2>
                <p className="mt-5 text-cream/70 max-w-md leading-relaxed">
                  Call ahead for takeaway or simply walk in — we're open seven days a
                  week in the heart of Pune.
                </p>
              </div>
              <div className="flex flex-wrap gap-4 lg:justify-end">
                <a
                  href={`tel:${RESTAURANT.phoneRaw}`}
                  className="inline-flex items-center gap-2 rounded-full bg-cream text-brand hover:bg-cream/90 px-7 py-3.5 text-sm font-medium tracking-wide transition-colors"
                >
                  <Phone className="w-4 h-4" /> {RESTAURANT.phone}
                </a>
                <Link
                  to="/contact-us"
                  className="inline-flex items-center gap-2 rounded-full border border-cream/35 text-cream hover:bg-cream/10 px-7 py-3.5 text-sm font-medium tracking-wide transition-colors"
                >
                  Find Us <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
